import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PosterCard from "@/components/base/PosterCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getHot, searchAll } from "@/lib/media";

type Tab = "all" | "movie" | "tv" | "news";

const tabs: { value: Tab; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
  { value: "news", label: "资讯" },
];

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [tab, setTab] = useState<Tab>("all");

  usePageMeta(
    q ? `“${q}” 的搜索结果 - 欧美日韩影视平台` : "搜索 - 欧美日韩影视平台",
    `在欧美日韩影视平台搜索“${q}”相关的电影、电视剧与影视资讯。`,
    "影视搜索,搜索影片,搜索剧集,影视资讯搜索",
  );

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const { titles, news } = useMemo(() => searchAll(q), [q]);

  const movieResults = titles.filter((t) => t.type === "movie");
  const tvResults = titles.filter((t) => t.type === "tv");
  const total = titles.length + news.length;

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const k = keyword.trim();
    if (!k) return;
    window.REACT_APP_NAVIGATE?.(`/search?q=${encodeURIComponent(k)}`);
  };

  const showTitles =
    tab === "all" ? titles : tab === "movie" ? movieResults : tab === "tv" ? tvResults : [];
  const showNews = tab === "all" || tab === "news" ? news : [];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-8">
        <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
          <h1 className="text-xl md:text-2xl font-extrabold text-foreground-950 font-heading mb-4">
            搜索影视内容
          </h1>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入影片名、剧集、导演或资讯关键词"
                className="w-full h-11 pl-10 pr-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          {q && (
            <p className="mt-3 text-sm text-foreground-500">
              关键词：<span className="font-semibold text-primary-600">{q}</span> · 共找到{" "}
              <span className="font-semibold text-accent-600">{total}</span> 条结果
            </p>
          )}
        </div>

        {!q ? (
          <div className="py-20 flex flex-col items-center text-center">
            <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
              <i className="ri-search-2-line text-4xl text-foreground-400" />
            </span>
            <h2 className="text-lg font-bold text-foreground-800 font-heading">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 text-sm text-foreground-500 max-w-md">
              可以搜索电影、电视剧的名称、导演、演员、类型，也可以查找影视资讯。
            </p>
          </div>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-2 border-b border-background-200">
              {tabs.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTab(t.value)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap cursor-pointer ${
                    tab === t.value
                      ? "border-accent-500 text-primary-700"
                      : "border-transparent text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {t.label}
                  <span className="ml-1 text-xs text-foreground-400">
                    {t.value === "all"
                      ? total
                      : t.value === "movie"
                        ? movieResults.length
                        : t.value === "tv"
                          ? tvResults.length
                          : news.length}
                  </span>
                </button>
              ))}
            </div>

            {total === 0 ? (
              <div className="py-20 flex flex-col items-center text-center">
                <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-400" />
                </span>
                <h2 className="text-lg font-bold text-foreground-800 font-heading">
                  没有找到「{q}」相关的内容
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  换个关键词试试，或看看下面这些热门推荐
                </p>
                <div className="mt-8 w-full text-left">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-4">大家还在看</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                    {getHot(5).map((item) => (
                      <PosterCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-8 pb-4">
                {showTitles.length > 0 && (
                  <section>
                    <h2 className="text-base font-bold text-foreground-950 font-heading mb-4">
                      {tab === "movie" ? "电影" : tab === "tv" ? "电视剧" : "影视作品"}
                      <span className="ml-2 text-sm font-normal text-foreground-400">
                        {showTitles.length} 部
                      </span>
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                      {showTitles.map((item) => (
                        <PosterCard key={item.id} item={item} />
                      ))}
                    </div>
                  </section>
                )}

                {showNews.length > 0 && (
                  <section>
                    <h2 className="text-base font-bold text-foreground-950 font-heading mb-4">
                      影视资讯
                      <span className="ml-2 text-sm font-normal text-foreground-400">
                        {showNews.length} 篇
                      </span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {showNews.map((n) => (
                        <Link
                          key={n.id}
                          to={`/news/${n.id}`}
                          className="group flex gap-4 bg-background-50 border border-background-200 rounded-lg p-3 hover:border-primary-300 transition-colors"
                        >
                          <div className="w-28 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                            <img
                              src={n.cover}
                              alt={n.title}
                              title={`${n.title} 影视资讯`}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {n.title}
                            </h3>
                            <p className="mt-1 text-xs text-foreground-500 line-clamp-1">
                              {n.summary}
                            </p>
                            <span className="text-[11px] text-foreground-400 mt-1.5 inline-block">
                              {n.date}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}